import { isPromoInDateRange } from '@/config/promo';
import type { PromoConfig } from '@/types/promo';

interface ActivePromoResponse {
  /** Format actuel : tableau de promos */
  promos?: unknown[];
  /** Format hérité : promo unique (compatibilité ascendante) */
  promo?: unknown;
}

const ACTIVE_PROMO_FUNCTION_PATH = '/.netlify/functions/active-promo';

function isPromoShape(value: unknown): value is PromoConfig {
  if (!value || typeof value !== 'object') return false;

  const maybePromo = value as PromoConfig;
  return Boolean(maybePromo.code && maybePromo.startDate);
}

function normalizePromo(value: unknown): PromoConfig | null {
  if (!isPromoShape(value)) return null;
  if (!isPromoInDateRange(value)) return null;
  return value;
}

export async function fetchRuntimeActivePromos(): Promise<PromoConfig[]> {
  if (typeof window === 'undefined') return [];

  try {
    const response = await fetch(ACTIVE_PROMO_FUNCTION_PATH, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Active promo request failed: ${response.status}`);
    }

    const payload = (await response.json()) as ActivePromoResponse;

    // Format actuel : tableau de promos
    if (Array.isArray(payload?.promos)) {
      return payload.promos
        .map(normalizePromo)
        .filter((p): p is PromoConfig => p !== null);
    }

    // Format hérité : promo unique — compatibilité ascendante
    const single = normalizePromo(payload?.promo);
    return single ? [single] : [];
  } catch {
    // En cas d'erreur réseau, on désactive silencieusement les promos.
    return [];
  }
}
